import { FC, useEffect } from 'react';
import { DateTime, Settings } from 'luxon';
import { useSnackbar } from 'notistack';
import DayHourPicker from 'src/components/Items/input/DayHourPicker';
import useIsMobile from 'src/hooks/useIsMobile';
import { changeISOset, fromISOtoFormat, fromJSDateToISO } from 'src/common/utils/dataFormat';

Settings.defaultLocale = 'UTC';
Settings.defaultZone = 'UTC';
Settings.defaultOutputCalendar = 'UTC';

interface IProps {
  check: boolean;
  startDate?: string;
  endDate?: string;
  handleStartDate: (arg0: string) => void;
  handleEndDate: (arg0: string) => void;
  className?: string;
}

const Times: FC<IProps> = ({
  check,
  startDate,
  endDate,
  handleStartDate,
  handleEndDate,
  className
}) => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const isMobile = useIsMobile();

  const checkLess = (start?: string, end?: string) => {
    if (start && end) {
      return start >= end;
    } else return false;
  };

  useEffect(() => {
    const nowTimePlusDay = DateTime.now()
      .plus({ hours: 24 })
      .set({ second: 0, millisecond: 0 })
      .toISO();
    checkLess(nowTimePlusDay, startDate) &&
      (enqueueSnackbar(
        'Starting in the next 24h’s? We’ll email how to call/text your host to arrange details.',
        {
          variant: 'info',
          persist: true
        }
      ),
      setTimeout(() => closeSnackbar(), 5000));
  }, [startDate]);

  const setStartDate = (date?: Date, hour?: string) => {
    if (date && !hour) {
      let isoDate = fromJSDateToISO(date);
      const nowTime = DateTime.now().toFormat('D');
      if (fromISOtoFormat(isoDate, 'D') === nowTime) {
        handleStartDate(DateTime.now().set({ second: 0, millisecond: 0 }).toISO());
      } else {
        isoDate = changeISOset(isoDate, {
          hour: 10,
          minute: 0,
          second: 0
        });
        handleStartDate(isoDate);
        if (checkLess(isoDate, endDate!)) {
          const startObj = DateTime.fromISO(isoDate)
            .plus({ hours: 2 })
            .set({ second: 0, millisecond: 0 })
            .toISO();
          handleEndDate(startObj);
        }
      }
    }
    if (hour) {
      const isoDate = fromJSDateToISO(date ?? DateTime.now().toISO());
      let time = hour;
      if (time === 'Noon') {
        time = '00:00 PM';
      }
      if (time === 'Midnight') {
        time = '00:00 AM';
      }

      const dayPart = time.split(' ');
      if (dayPart[1] === 'AM') {
        const time = dayPart[0].split(':');
        const changeHour = changeISOset(isoDate, {
          hour: time[0] === '12' ? +time[0] - 12 : +time[0],
          minute: +time[1],
          second: 0
        });
        handleStartDate(changeHour);
        if (checkLess(changeHour, endDate!)) {
          const startObj = DateTime.fromISO(changeHour)
            .plus({ hours: 2 })
            .set({ second: 0, millisecond: 0 })
            .toISO();
          handleEndDate(startObj);
        }
      } else {
        const time = dayPart[0].split(':');
        const changeHour = changeISOset(isoDate, {
          hour: time[0] === '12' ? +time[0] : +time[0] + 12,
          minute: +time[1],
          second: 0
        });
        handleStartDate(changeHour);
        if (checkLess(changeHour, endDate!)) {
          const startObj = DateTime.fromISO(changeHour)
            .plus({ hours: 2 })
            .set({ second: 0, millisecond: 0 })
            .toISO();
          handleEndDate(startObj);
        }
      }
    }
  };

  const setEndDate = (date?: Date, hour?: string) => {
    if (checkLess(startDate, fromJSDateToISO(date)) && !hour) {
      const startObj = DateTime.fromISO(startDate!)
        .plus({ hours: 2 })
        .set({ second: 0, millisecond: 0 })
        .toISO();
      handleEndDate(startObj);
    } else {
      if (date && !hour) {
        let isoDate = fromJSDateToISO(date);
        const nowTime = DateTime.now().toFormat('D');
        if (fromISOtoFormat(isoDate, 'D') === nowTime) {
          if (startDate) {
            handleEndDate(
              DateTime.fromISO(startDate!)
                .plus({ hours: 2 })
                .set({ second: 0, millisecond: 0 })
                .toISO()
            );
          } else
            handleEndDate(
              DateTime.now().plus({ hours: 2 }).set({ second: 0, millisecond: 0 }).toISO()
            );
        } else if (fromISOtoFormat(isoDate, 'D') === fromISOtoFormat(startDate!, 'D')) {
          handleEndDate(
            DateTime.fromISO(startDate!)
              .plus({ hours: 2 })
              .set({ second: 0, millisecond: 0 })
              .toISO()
          );
        } else {
          isoDate = changeISOset(isoDate, {
            hour: 10,
            minute: 0,
            second: 0
          });
          handleEndDate(isoDate);
        }
      }
      if (hour) {
        const isoDate = fromJSDateToISO(date ?? DateTime.now().toISO());
        let time = hour;
        if (time === 'Midnight') {
          time = '00:00 AM';
        }
        if (time === 'Noon') {
          time = '00:00 PM';
        }
        const dayPart = time.split(' ');
        if (dayPart[1] === 'AM') {
          const time = dayPart[0].split(':');
          const changeHour = changeISOset(isoDate, {
            hour: time[0] === '12' ? +time[0] - 12 : +time[0],
            minute: +time[1],
            second: 0
          });
          handleEndDate(changeHour);
          if (checkLess(startDate, changeHour)) {
            const startObj = DateTime.fromISO(startDate!)
              .plus({ hours: 2 })
              .set({ second: 0, millisecond: 0 })
              .toISO();
            handleEndDate(startObj);
          }
        } else {
          const time = dayPart[0].split(':');
          const changeHour = changeISOset(isoDate, {
            hour: time[0] === '12' ? +time[0] : +time[0] + 12,
            minute: +time[1],
            second: 0
          });
          handleEndDate(changeHour);
          if (checkLess(startDate, changeHour)) {
            const startObj = DateTime.fromISO(startDate!)
              .plus({ hours: 2 })
              .set({ second: 0, millisecond: 0 })
              .toISO();
            handleEndDate(startObj);
          }
        }
      }
    }
  };

  return (
    <div className={className ? className : 'filter__time'}>
      <div className='data-picker'>
        <div className='filter__title'>Date From</div>
        <DayHourPicker date={startDate} setDate={setStartDate} isMobile={isMobile} check={check} />
      </div>
      {className !== 'singleVan__availability__formContainer__times' && (
        <div className='dash'>-</div>
      )}
      <div className='data-picker'>
        <div className='filter__title'>Date Drop</div>
        <DayHourPicker
          date={endDate}
          setDate={setEndDate}
          minLimit={startDate!}
          isMobile={isMobile}
          check={check}
        />
      </div>
    </div>
  );
};

export default Times;
